import { fmtKg, fmtTol } from "@/lib/utils";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ScanLine, Camera, CheckCircle2, Undo2, Printer, Pencil, Trash2, AlertTriangle, Loader2, Flag, X, FileText,
} from "lucide-react";
import {
  scanRmForPlan, unbindRmFromPlan, setPlanLineParts, completeSlittingPlan, deleteSlittingPlan, setPlanPriority, planLabelsPdfUrl, planLabelsTsplUrl, planJobCardPdfUrl,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import BarcodeScanner from "@/components/BarcodeScanner";
import PrintButton from "@/components/PrintButton";
import LabelPrinterStatus from "@/components/LabelPrinterStatus";
import { LinePriorityEdit } from "@/components/LinePriorityEdit";
import { useAuth } from "@/contexts/AuthContext";

const PlanDetail = ({ plan, onClose, onEdit }) => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isDirector = user?.role === "director";
  const isCompleted = plan.status === "completed";
  const hasBoundLine = plan.lines.some((l) => l.mother_coil_id);

  const [scanInput, setScanInput] = useState("");
  const [cameraOn, setCameraOn] = useState(false);
  const [lastDetected, setLastDetected] = useState("");
  const [completeOpen, setCompleteOpen] = useState(false);
  const [scrapStr, setScrapStr] = useState("");
  const [completeNotes, setCompleteNotes] = useState("");

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["slitting-plans"] });
    qc.invalidateQueries({ queryKey: ["slitting-plan", plan.id] });
    qc.invalidateQueries({ queryKey: ["coils"] });
    qc.invalidateQueries({ queryKey: ["stats"] });
  };

  const scanMut = useMutation({
    mutationFn: (barcode) => scanRmForPlan(plan.id, barcode),
    onSuccess: () => {
      toast.success("RM bound to plan ✓");
      setScanInput("");
      invalidate();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Scan failed"),
  });

  const unbindMut = useMutation({
    mutationFn: (lineId) => unbindRmFromPlan(plan.id, lineId),
    onSuccess: () => { toast.success("RM unbound — returned to inventory"); invalidate(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Unbind failed"),
  });

  const partsMut = useMutation({
    mutationFn: ({ lineId, parts }) => setPlanLineParts(plan.id, lineId, parts),
    onSuccess: (d) => { toast.success(d.num_parts > 1 ? `RM will be processed in ${d.num_parts} parts — SFG labels updated` : "Back to 1 part — SFG labels updated"); invalidate(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't change parts"),
  });

  const completeMut = useMutation({
    mutationFn: () => completeSlittingPlan(plan.id, parseFloat(scrapStr) || 0, completeNotes),
    onSuccess: () => {
      toast.success("Plan marked complete");
      setCompleteOpen(false);
      invalidate();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not complete"),
  });

  const priorityMut = useMutation({
    mutationFn: (priority) => setPlanPriority(plan.id, priority),
    onSuccess: () => { toast.success("Priority updated"); invalidate(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const deleteMut = useMutation({
    mutationFn: () => deleteSlittingPlan(plan.id),
    onSuccess: () => {
      toast.success("Plan deleted");
      onClose();
      invalidate();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not delete"),
  });

  const allBound = plan.lines.every((l) => l.mother_coil_id);
  const boundCount = plan.lines.filter((l) => l.mother_coil_id).length;

  const handleScan = (e) => {
    e?.preventDefault();
    if (!scanInput.trim()) return;
    scanMut.mutate(scanInput.trim());
  };

  return (
    <div data-testid="plan-detail" className="bg-white rounded-sm border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-heading text-2xl font-bold text-slate-900 font-mono tracking-tight" data-testid="plan-number">{plan.plan_number}</p>
            <span data-testid="plan-status-badge" className={`inline-flex items-center text-[10px] tracking-wider uppercase font-semibold px-2 py-0.5 rounded-sm ${
              isCompleted ? "bg-green-100 text-green-800 border border-green-200"
                : allBound ? "bg-blue-100 text-blue-800 border border-blue-200"
                  : "bg-amber-100 text-amber-800 border border-amber-200"
            }`}>
              {isCompleted ? "Completed" : allBound ? "All RMs scanned" : `${boundCount} of ${plan.lines.length} scanned`}
            </span>
            {plan.priority === "urgent" && (
              <span data-testid="urgent-badge" className="inline-flex items-center gap-1 text-[10px] tracking-wider uppercase font-bold px-2 py-0.5 rounded-sm bg-red-600 text-white">
                <Flag className="h-3 w-3" /> Urgent
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-1">Created {(plan.created_at || "").slice(0, 16).replace("T", " ")} by {plan.created_by}</p>
        </div>
        <button data-testid="close-detail-btn" onClick={onClose} className="text-slate-400 hover:text-slate-900 p-1">
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Action bar */}
      <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex flex-wrap items-center gap-2">
        <LabelPrinterStatus testId="plan-label-printer-status" />
        {!isCompleted && !hasBoundLine && (
          <span
            data-testid="print-labels-locked"
            className="inline-flex items-center gap-1.5 text-xs text-slate-500 border border-dashed border-slate-300 bg-white rounded-sm px-3 py-1.5"
            title="Each RM shows its own Print SFG button once scanned — weights are only known then."
          >
            <Printer className="h-3.5 w-3.5" /> SFG print buttons appear per RM after scan
          </span>
        )}
        {hasBoundLine && (
          <Button asChild variant="outline" size="sm" data-testid="job-card-pdf-btn">
            <a href={planJobCardPdfUrl(plan.id)} target="_blank" rel="noreferrer">
              <FileText className="h-4 w-4 mr-1.5" /> Job Card PDF
            </a>
          </Button>
        )}
        {!isCompleted && (
          <Button variant="outline" size="sm" data-testid="edit-plan-btn" onClick={onEdit}>
            <Pencil className="h-4 w-4 mr-1.5" /> Edit
          </Button>
        )}
        {!isCompleted && allBound && (
          <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" data-testid="mark-complete-btn" onClick={() => setCompleteOpen(true)}>
            <CheckCircle2 className="h-4 w-4 mr-1.5" /> Mark Complete
          </Button>
        )}
        {isDirector && !isCompleted && (
          <Button
            variant="outline" size="sm"
            data-testid="toggle-priority-btn"
            onClick={() => priorityMut.mutate(plan.priority === "urgent" ? "normal" : "urgent")}
            disabled={priorityMut.isPending}
          >
            <Flag className="h-4 w-4 mr-1.5" /> {plan.priority === "urgent" ? "Un-mark urgent" : "Mark urgent"}
          </Button>
        )}
        {!isCompleted && !hasBoundLine && (
          <Button variant="outline" size="sm" className="text-red-700 border-red-200 hover:bg-red-50" data-testid="delete-plan-btn"
            onClick={() => { if (window.confirm(`Delete plan ${plan.plan_number} and its ${plan.sfgs?.length || 0} pre-issued SFGs?`)) deleteMut.mutate(); }}
            disabled={deleteMut.isPending}
          >
            <Trash2 className="h-4 w-4 mr-1.5" /> Delete
          </Button>
        )}
      </div>

      {/* Scan RM */}
      {!isCompleted && !allBound && (
        <div className="px-5 py-4 border-b border-slate-200 bg-blue-50/40">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <p className="font-heading text-sm font-bold text-slate-900 flex items-center gap-2">
              <ScanLine className="h-4 w-4" /> Scan or type an RM barcode
            </p>
            <Button
              type="button"
              variant={cameraOn ? "default" : "outline"}
              size="sm"
              data-testid="toggle-camera-btn"
              onClick={() => { setCameraOn((on) => !on); setLastDetected(""); }}
              className={cameraOn ? "bg-slate-900 hover:bg-slate-800 text-white" : ""}
            >
              <Camera className="h-4 w-4 mr-1.5" /> {cameraOn ? "Stop camera" : "Use camera"}
            </Button>
          </div>

          {cameraOn && (
            <div className="mb-3" data-testid="scan-camera-wrap">
              <BarcodeScanner
                active={cameraOn && !scanMut.isPending}
                onDetected={(text) => {
                  const code = (text || "").trim();
                  if (!code || code === lastDetected || scanMut.isPending) return;
                  setLastDetected(code);
                  setScanInput(code);
                  scanMut.mutate(code);
                }}
              />
              <p className="text-[11px] text-slate-500 mt-1.5">
                Point the camera at the RM Code-128 barcode. Auto-binds when detected.
              </p>
            </div>
          )}

          <form onSubmit={handleScan} className="flex gap-2">
            <input
              data-testid="scan-rm-input"
              value={scanInput}
              onChange={(e) => setScanInput(e.target.value)}
              placeholder="e.g. RM-3"
              className="flex-1 h-11 px-3 border border-slate-300 rounded-sm font-mono text-sm uppercase"
              autoComplete="off"
            />
            <Button data-testid="scan-rm-submit" type="submit" disabled={!scanInput.trim() || scanMut.isPending} className="h-11 px-5 bg-slate-900 hover:bg-slate-800 text-white">
              {scanMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Bind to plan</>}
            </Button>
          </form>
          <p className="text-[11px] text-slate-500 mt-2">System will auto-match by grade + thickness to the first pending line.</p>
        </div>
      )}

      {/* Lines */}
      <div className="px-5 py-4 space-y-3" data-testid="plan-lines">
        <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold">RM Lines · {plan.lines.length}</p>
        {plan.lines.map((line, idx) => {
          const bound = !!line.mother_coil_id;
          const totalSlit = (line.slit_plan || []).reduce((acc, r) => acc + r.slit_width_mm * r.count, 0);
          const totalCount = (line.slit_plan || []).reduce((acc, r) => acc + r.count, 0);
          const lineSfgs = (plan.sfgs || []).filter((s) => s.plan_line_id === line.line_id);
          return (
            <div
              key={line.line_id}
              data-testid={`detail-line-${idx}`}
              className={`border rounded-sm p-3 ${bound ? "border-green-300 bg-green-50/30" : "border-slate-200 bg-white"}`}
            >
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-sm font-bold text-slate-900">RM #{idx + 1}</span>
                  {!isCompleted && (
                    <span data-testid={`detail-line-${idx}-priority-no`}>
                      <LinePriorityEdit planId={plan.id} line={line} idx={idx} onSaved={invalidate} />
                    </span>
                  )}
                  <span className="text-[10px] tracking-wider uppercase font-semibold px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-700 border border-slate-200">
                    {line.target_grade} · {line.target_thickness_mm}mm · {line.target_width_mm}mm
                  </span>
                  <span data-testid={`detail-line-${idx}-tol-parts`} className="text-[10px] tracking-wider uppercase font-semibold px-1.5 py-0.5 rounded-sm bg-blue-50 text-blue-800 border border-blue-200">
                    Tol {fmtTol(line.tolerance_mm)} mm · {line.num_parts || 1} part{(line.num_parts || 1) !== 1 ? "s" : ""}
                  </span>
                  {bound ? (
                    <span data-testid={`bound-${idx}`} className="inline-flex items-center gap-1 text-[10px] tracking-wider uppercase font-bold px-1.5 py-0.5 rounded-sm bg-green-600 text-white">
                      <CheckCircle2 className="h-3 w-3" /> {line.mother_coil_id} ({fmtKg(line.actual_rm_weight_kg)} kg)
                    </span>
                  ) : (
                    <span className="inline-flex items-center text-[10px] tracking-wider uppercase font-semibold px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800 border border-amber-200">
                      Pending scan
                    </span>
                  )}
                </div>
                {bound && !isCompleted && (
                  <div className="flex items-center gap-2">
                    <PrintButton
                      testId={`print-labels-rm-${idx}`}
                      tspl={planLabelsTsplUrl(plan.id, line.mother_coil_id)}
                      pdf={planLabelsPdfUrl(plan.id, line.mother_coil_id)}
                      label={`SFG labels · ${line.mother_coil_id}`}
                    >
                      Print SFG labels
                    </PrintButton>
                    <button
                      data-testid={`unbind-${idx}`}
                      onClick={() => { if (window.confirm(`Unbind ${line.mother_coil_id} and return it to inventory?`)) unbindMut.mutate(line.line_id); }}
                      disabled={unbindMut.isPending}
                      className="text-xs text-amber-700 hover:text-amber-900 hover:underline flex items-center gap-1"
                    >
                      <Undo2 className="h-3 w-3" /> Undo
                    </button>
                  </div>
                )}
                {bound && isCompleted && (
                  <PrintButton
                    testId={`print-labels-rm-${idx}`}
                    tspl={planLabelsTsplUrl(plan.id, line.mother_coil_id)}
                    pdf={planLabelsPdfUrl(plan.id, line.mother_coil_id)}
                    label={`SFG labels · ${line.mother_coil_id}`}
                  >
                    Print SFG labels
                  </PrintButton>
                )}
              </div>

              <p className="text-xs text-slate-600 mt-2">
                Cut: {(line.slit_plan || []).map((r, i) => (
                  <span key={`${r.slit_width_mm}-${r.count}-${i}`}>{i > 0 ? " + " : ""}{r.count}×{r.slit_width_mm}mm</span>
                ))}
                <span className="text-slate-400"> · total {totalSlit}mm · {totalCount} SFG{totalCount !== 1 ? "s" : ""}</span>
              </p>

              {lineSfgs.length > 0 && (
                <div className="mt-3 pt-3 border-t border-dashed border-slate-200" data-testid={`line-${idx}-sfgs`}>
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <p className="text-[9px] tracking-[0.22em] uppercase text-slate-400 font-semibold">SFGs from this RM · {lineSfgs.length}</p>
                    {bound && !isCompleted && (
                      <Button
                        type="button" size="sm" variant={(line.num_parts || 1) === 2 ? "outline" : "default"}
                        data-testid={`line-${idx}-plan-2-parts-btn`}
                        disabled={partsMut.isPending}
                        onClick={() => partsMut.mutate({ lineId: line.line_id, parts: (line.num_parts || 1) === 2 ? 1 : 2 })}
                        className="h-7 text-xs"
                      >
                        {partsMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (line.num_parts || 1) === 2 ? "Back to 1 part" : "Plan in 2 parts"}
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1.5">
                    {lineSfgs.map((s) => (
                      <div key={s.coil_id} data-testid={`sfg-${s.coil_id}`} className="bg-white border border-slate-200 rounded-sm px-2 py-1">
                        <p className="font-mono text-xs font-bold text-slate-900">{s.coil_id}</p>
                        <p className="text-[10px] text-slate-500">
                          {s.width_mm}mm · {fmtKg(s.weight_kg)} kg{bound ? "" : " · pending RM"}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer with completion meta */}
      {isCompleted && (
        <div className="px-5 py-4 border-t border-slate-200 bg-green-50/40 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          <div><p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Completed</p><p className="font-mono mt-1">{(plan.completed_at || "").slice(0, 16).replace("T", " ")}</p></div>
          <div><p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">By</p><p className="font-mono mt-1">{plan.completed_by}</p></div>
          <div><p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Total RM Wt</p><p className="font-mono mt-1">{fmtKg(plan.total_rm_weight_kg)} kg</p></div>
          <div><p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Scrap</p><p className="font-mono mt-1 text-orange-700 font-bold">{plan.scrap_kg} kg</p></div>
        </div>
      )}

      {/* Complete dialog overlay */}
      {completeOpen && (
        <div data-testid="complete-dialog" className="absolute inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-10">
          <div className="bg-white rounded-sm max-w-md w-full p-5">
            <p className="font-heading text-xl font-bold text-slate-900">Mark plan complete</p>
            <p className="text-sm text-slate-600 mt-1">All {plan.lines.length} RMs scanned. Enter the actual scrap weight in kg for this plan.</p>
            <div className="mt-4">
              <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Wastage / Scrap kg</label>
              <input
                data-testid="scrap-input"
                type="number" step="0.1" min="0"
                value={scrapStr}
                onChange={(e) => setScrapStr(e.target.value)}
                placeholder="0"
                className="mt-1 w-full h-12 px-3 border border-slate-300 rounded-sm font-mono text-base"
                autoFocus
              />
            </div>
            <div className="mt-3">
              <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Notes (optional)</label>
              <input
                data-testid="complete-notes"
                value={completeNotes}
                onChange={(e) => setCompleteNotes(e.target.value)}
                placeholder=""
                className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm"
              />
            </div>
            {parseFloat(scrapStr) < 0 && (
              <p className="text-xs text-red-700 mt-2 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Scrap cannot be negative</p>
            )}
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => setCompleteOpen(false)} disabled={completeMut.isPending} data-testid="complete-cancel">Cancel</Button>
              <Button
                onClick={() => completeMut.mutate()}
                disabled={completeMut.isPending || scrapStr === "" || parseFloat(scrapStr) < 0}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="complete-confirm"
              >
                {completeMut.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</> : "Confirm complete"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanDetail;
